import { 
  Users, 
  Calendar, 
  FileText,
  User,
  List,
  Clock,
  Tag
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { List as ListType } from "@/types/list";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ListOverviewCardProps {
  list: ListType;
  customFieldsConfig?: any[]; // Custom fields configuration for the organization
}

export const ListOverviewCard: React.FC<ListOverviewCardProps> = ({ 
  list,
  customFieldsConfig = []
}) => {
  const { t } = useTranslation();
  const [createdByName, setCreatedByName] = useState<string>("");

  // Fetch creator name from profiles table
  useEffect(() => {
    const fetchCreatedByName = async () => {
      if (list.created_by) {
        // Use the new database function that provides full access to user names
        const { data, error } = await supabase
          .rpc('get_user_display_info', {
            p_user_id: list.created_by
          });
        
        if (!error && data && data.length > 0) {
          const userInfo = data[0];
          setCreatedByName(`${userInfo.first_name || ""} ${userInfo.last_name || ""}`.trim());
        } else {
          setCreatedByName("");
        }
      } else {
        setCreatedByName("");
      }
    };
    fetchCreatedByName();
  }, [list.created_by]);

  // Helper to check if custom fields have values (only for currently configured fields)
  const hasCustomFieldsConfigured = customFieldsConfig.length > 0;
  const hasCustomFieldData = list.custom_fields && Object.keys(list.custom_fields).length > 0;

  // Helper function to format custom field values
  const formatCustomFieldValue = (fieldName: string, value: any) => {
    if (value === null || value === undefined) return '-';
    
    // Find the field configuration to determine the type
    const fieldConfig = customFieldsConfig.find(f => f.name === fieldName);
    if (!fieldConfig) return String(value);

    switch (fieldConfig.type) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'dropdown':
        return Array.isArray(value) ? value.join(', ') : String(value);
      case 'number':
        return String(value);
      default:
        return String(value);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  // Helper function to check if a section has data to display
  const hasData = (values: (string | string[] | undefined | null)[]) => {
    return values.some(value => {
      if (Array.isArray(value)) return value && value.length > 0;
      return !!value;
    });
  };

  // Check which sections have data
  const hasDescription = hasData([list.description]);
  const hasGeneral = hasData([list.created_by, list.created_at]);
  const hasStats = hasData([list.candidate_count, list.created_at]);

  return (
    <div className="space-y-8">
      {/* Header Section - List Name prominently displayed */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 bg-blue-100 dark:bg-blue-900/30 rounded-sm flex items-center justify-center">
            <List className="h-8 w-8 text-blue-500" />
          </div>
          <div className="flex flex-col space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {list.name}
            </h1>
            {list.description && (
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                {list.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Column 1: List Statistics */}
        <div className="space-y-4">
          {hasStats && (
            <div className="bg-white dark:bg-gray-800 rounded-sm p-4 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: 'rgba(34, 197, 94, 0.03)' }}>
              <h3 className="text-sm font-semibold flex items-center mb-3">
                <Users className="h-4 w-4 text-green-600 mr-2" />
                {t("lists.overview.statistics", "Statistics")}
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                    <Users className="h-3 w-3" />
                    {t("lists.overview.candidateCount", "Candidates")}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {list.candidate_count} {t("lists.overview.candidates", "candidates")}
                  </span>
                </div>
                
                <div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                    <Calendar className="h-3 w-3" />
                    {t("lists.overview.created", "Created")}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {formatDate(list.created_at)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Column 2: General Information */}
        <div className="space-y-4">
          {hasGeneral && (
            <div className="bg-white dark:bg-gray-800 rounded-sm p-4 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: 'rgba(59, 130, 246, 0.03)' }}>
              <h3 className="text-sm font-semibold flex items-center mb-3">
                <FileText className="h-4 w-4 text-blue-500 mr-2" />
                {t("lists.overview.general", "General")}
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                    <Clock className="h-3 w-3" />
                    {t("lists.overview.lastUpdated", "Last updated")}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {formatDate(list.updated_at)}
                  </span>
                </div>
                
                {list.created_by && (
                  <div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                      <User className="h-3 w-3" />
                      {t("lists.overview.createdBy", "Created by")}
                    </span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {createdByName || `User ${list.created_by.substring(0, 8)}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>



        {/* Column 3: Custom Fields (if present) */}
        {hasCustomFieldsConfigured && hasCustomFieldData && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-sm p-4 border border-gray-200 dark:border-gray-700" style={{ backgroundColor: 'rgba(239, 68, 68, 0.03)' }}>
              <h3 className="text-sm font-semibold flex items-center mb-3">
                <Tag className="h-4 w-4 text-red-600 mr-2" />
                {t("lists.customFields.title", "Custom Fields")}
              </h3>
              <div className="space-y-3">
                {customFieldsConfig.map((fieldConfig) => {
                  const fieldValue = list.custom_fields?.[fieldConfig.name];
                  
                  // For boolean fields, show even if false. For other fields, skip if empty/falsy
                  if (fieldConfig.type !== 'boolean' && !fieldValue) return null;
                  
                  return (
                    <div key={fieldConfig.name}>
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block flex items-center gap-2">
                        <Tag className="h-3 w-3" />
                        {fieldConfig.name}
                      </span>
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {formatCustomFieldValue(fieldConfig.name, fieldValue)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
